'use client'

import { AbsoluteCenter, Box, Button, Center, CircularProgress, Divider, Flex, FormControl, FormErrorMessage, FormLabel, HStack, Heading, Icon, Input, InputGroup, InputRightElement, Link, Stack, Text, useColorModeValue, useToast } from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { useRef, useState } from "react";
import { ClientSafeProvider, LiteralUnion, getProviders, signIn } from "next-auth/react"
import { BsEyeFill, BsFillEyeSlashFill } from 'react-icons/bs';
import { FcGoogle } from 'react-icons/fc'
import NextLink from "next/link";
import { useRouter } from "next/navigation";
import { BuiltInProviderType } from "next-auth/providers/index";
import ReCAPTCHA from "react-google-recaptcha";
import { useAction } from "next-safe-action/hook";
import { verifyCaptchaToken } from "./actions";

interface SignInSchema {
    email: string;
    password: string;
}

interface SignInFormProps {
    providers: Record<LiteralUnion<BuiltInProviderType, string>, ClientSafeProvider> | null
}

export default function SignInForm({ providers }: SignInFormProps) {

    const { handleSubmit, getValues, reset, register, formState: { errors, isSubmitting } } = useForm<SignInSchema>()
    const toast = useToast()
    const router = useRouter()

    const [isSignInLoading, setIsSignInLoading] = useState(false)

    const [token, setToken] = useState('')
    const recaptchaRef = useRef<any>(null)
    const onCaptchaChange = (token: string | null) => {
        if (token) {
            setToken(token)
        }
    }

    const [showPassword, setShowPassword] = useState(false)

    const { execute, status } = useAction(verifyCaptchaToken, {
        onSuccess: async (data) => {
            // console.log(data);

            if (data?.failure) {
                toast({
                    title: 'Une erreur est survenue!',
                    description: data?.failure.toString(),
                    status: 'error',
                    isClosable: true,
                    position: 'top'
                })
            } else if (data?.success) {
                const values = getValues()
                console.log("Test Val! ", values);

                await handleSignIn(values)
            }
        },
        onError: (error) => {
            // console.log("Une erreur: ", error);

            toast({
                title: 'Error',
                description: 'Veuillez vérifier vos paramètres',
                status: 'error',
                isClosable: true,
                position: 'top'
            })
        }
    });

    async function handleForm(values: SignInSchema) {
        if (!token) return;
        execute({ captchaToken: token })
    }

    async function handleSignIn(values: SignInSchema) {
        setIsSignInLoading(true)
        const res = await signIn("credentials", {
            email: values.email,
            password: values.password,
            redirect: false
        })

        console.log("My res", res);


        if (res && res.ok) {
            toast({
                title: 'Opération réussie!',
                status: 'success',
                isClosable: true,
                position: 'top'
            })

            router.push("/dashboard")
        } else {
            if (res?.error == "Unverified!") {
                toast({
                    title: 'Une erreur est survenue',
                    description: 'Compte non vérifié. Veuillez consulter votre mail pour valider votre compte',
                    status: 'warning',
                    isClosable: true,
                    position: 'top'
                })
            } else if (res?.error == "NotValid!") {
                toast({
                    title: 'Une erreur est survenue',
                    description: 'Email ou mot de passe invalide. Veuillez vérifier vos paramètres',
                    status: 'error',
                    isClosable: true,
                    position: 'top'
                })
            } else {
                toast({
                    title: 'Une erreur est survenue',
                    description: 'Compte introuvable. Veuillez vérifier vos paramètres',
                    status: 'error',
                    isClosable: true,
                    position: 'top'
                })
            }

        }
        recaptchaRef?.current?.reset()
        setToken('')
        setIsSignInLoading(false)
    }

    return (
        <Flex
            minH="100vh"
            align="center"
            justify="center"
            bg={useColorModeValue('gray.100', 'gray.800')}
        >
            <form onSubmit={handleSubmit(handleForm)}>
                <Stack spacing={8} mx="auto" maxW="xl" py={12} px={6}>
                    <Stack align="center">
                        <Heading fontSize="4xl" textAlign="center">Se connecter à votre compte</Heading>
                        <Text fontSize="lg" color="gray.600">
                            pour profiter de nos <Link href="#" color="blue.500">fonctionnalités</Link> ✌️
                        </Text>
                    </Stack>
                    <Box
                        rounded="lg"
                        bg={useColorModeValue('white', 'gray.700')}
                        boxShadow="lg"
                        p={8}
                    >
                        <Stack spacing={4}>
                            <FormControl id="email" isInvalid={!!errors.email}>
                                <FormLabel>Email</FormLabel>
                                <Input
                                    type="email"
                                    placeholder='Enter email'
                                    {
                                    ...register('email', {
                                        required: 'Veuillez renseigner votre email',
                                        setValueAs: v => v.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;')
                                    })
                                    }
                                />
                                <FormErrorMessage>
                                    {errors['email'] && errors['email'].message}
                                </FormErrorMessage>
                            </FormControl>

                            <FormControl id="password" isInvalid={!!errors.password}>
                                <FormLabel>
                                    <Flex justifyContent="space-between">
                                        <Text>Password</Text>
                                        <Link as={NextLink} href="/auth/forgot-password" color="brand.500">mot de passe oublié?</Link>
                                    </Flex>
                                </FormLabel>
                                <InputGroup size='md'>
                                    <Input
                                        pr='4.5rem'
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder='Enter password'
                                        {
                                        ...register('password', {
                                            required: 'Veuillez renseigner votre mot de passe',
                                            setValueAs: v => v.trim().replace(/</g, '&lt;').replace(/>/g, '&gt;')
                                        })
                                        }
                                    />
                                    <InputRightElement width='4.5rem'>
                                        <Button h='1.75rem' size='sm' onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <Icon as={BsEyeFill} /> : <Icon as={BsFillEyeSlashFill} />}
                                        </Button>
                                    </InputRightElement>
                                </InputGroup>
                                <FormErrorMessage>
                                    {errors['password'] && errors['password'].message}
                                </FormErrorMessage>
                            </FormControl>
                        </Stack>

                        <Box mt={5}>
                            <ReCAPTCHA
                                size="normal"
                                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY ?? "NOT DEFINED"}
                                onChange={onCaptchaChange}
                                ref={recaptchaRef}
                            />
                        </Box>

                        <Stack spacing={7}>
                            <Button
                                type="submit"
                                mt="7"
                                colorScheme="brand"
                                isLoading={(status && status === "executing") || isSignInLoading}
                                isDisabled={!token}
                            >
                                Se connecter
                            </Button>
                            {!!providers && <>
                                {!!Object.values(providers).length && <Box position='relative'>
                                    <Divider />
                                    <AbsoluteCenter bg='white' px='4'>
                                        Ou
                                    </AbsoluteCenter>
                                </Box>}
                                {Object.values(providers).filter((provider) => provider.id !== "credentials").map((provider) => (
                                    <div key={provider.id}>

                                        {provider.id == "google" ? (
                                            <Button onClick={() => signIn(provider.id)} w={'full'} maxW={'md'} variant={'outline'} leftIcon={<FcGoogle />}>
                                                <Center>
                                                    <Text>Se connecter avec Google</Text>
                                                </Center>
                                            </Button>
                                        ) : (
                                            <Button colorScheme="brand" variant="outline" onClick={() => signIn(provider.id)}>
                                                Se connecter avec {provider.name}
                                            </Button>
                                        )}
                                    </div>

                                ))}
                            </>}
                        </Stack>
                        <Stack pt={6}>
                            <Text align="center">
                                Pas encore de compte? <Link as={NextLink} href="/auth/signup" color="blue.500" >{"S'inscrire"}</Link>
                            </Text>
                        </Stack>
                    </Box>
                </Stack>

                {/* {isSignInLoading && (
                    <Flex gap={2} justifyContent="center">
                        <CircularProgress size={5} isIndeterminate color='gray.300' />
                        <Text textAlign="center">Chargement...</Text>
                    </Flex>
                )} */}
            </form>
        </Flex>
    )
}